import React, { useEffect, useState } from "react";
import API from "../../api";
import {
  RefreshCw,
  Trash2,
  Search,
  Plus,
  MessageCircle,
  Download,
} from "lucide-react";
import AddProjectModal from "./AddProjectModal";
import RenewProjectModal from "./RenewProjectModal";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faWhatsapp } from '@fortawesome/free-brands-svg-icons';
import * as XLSX from "xlsx";

const ProjectDetails = () => {
  const [projects, setProjects] = useState([]);
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("");
  const [technology, setTechnology] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [renewModalOpen, setRenewModalOpen] = useState(false);
  const [selectedProjectId, setSelectedProjectId] = useState(null);

  const allCategories = [
    "Agency", "Banking", "Billing System", "Blog", "Construction",
    "Consulting", "Corporate", "CRM", "Digital Marketing", "Domain Management",
    "Educational", "Entertainment", "E-Commerce", "ERP", "Event Management",
    "Finance", "Fitness", "Food Delivery", "Government", "Healthcare",
    "Hosting", "Hospital", "Inventory Management", "Landing Page", "Logistics",
    "Manufacturing", "MLM", "Mobile App", "News Portal", "NGO", "Other",
    "Portfolio", "POS System", "Real Estate", "Retail", "SEO", "Server Maintenance",
    "Software", "Startup", "Travel", "Web App", "Website", "Wholesale"
  ].sort();

  const allTechnologies = [
    "React", "Node.js", "Angular", "Vue", "Laravel", "PHP", "Python", "Django",
    "Flask", "Java", "Spring", "C#", ".NET", "WordPress", "Shopify", "Other"
  ].sort();

  const fetchProjects = async () => {
    try {
      setLoading(true);
      const { data } = await API.get("/admin/projects", {
        params: { search, category, technology, statusFilter },
      });
      setProjects(data.projects || []);
    } catch (err) {
      console.error("Error fetching projects:", err);
      alert("Failed to fetch projects");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProjects();
  }, [search, category, technology, statusFilter]);

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this project?")) return;
    try {
      await API.delete(`/admin/projects/${id}`);
      alert("✅ Project deleted");
      fetchProjects();
    } catch (err) {
      console.error(err);
      alert("❌ Failed to delete project");
    }
  };

  const handleShareWhatsApp = (domain) => {
    const message = `🌐 Project Domain: ${domain}`;
    const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, "_blank");
  };

  const handleDownloadExcel = () => {
    if (projects.length === 0) {
      alert("No data to export");
      return;
    }
    const worksheet = XLSX.utils.json_to_sheet(
      projects.map((p) => ({
        Customer: p.customer_name,
        Contact: p.contact_number,
        Project: p.project_name,
        Category: p.project_category,
        Technology: p.project_technology,
        Domain: p.domain_name,
        PurchaseDate: p.domain_purchase_date,
        Server: p.server_details,
        Status: p.domain_status,
      }))
    );
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Projects");
    XLSX.writeFile(workbook, "Project_Details.xlsx");
  };

  const badgeClass = (status) => {
    switch (status) {
      case "expired":
        return "bg-red-100 text-red-600";
      case "expire soon":
        return "bg-yellow-100 text-yellow-600";
      default:
        return "bg-green-100 text-green-600";
    }
  };

  const formatIndiaDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString("en-GB", {
      timeZone: "Asia/Kolkata",
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-center mb-6 gap-3">
        <h1 className="text-2xl font-bold text-gray-800">Project Details</h1>
        <div className="flex gap-3">
          <button
            onClick={handleDownloadExcel}
            className="flex items-center gap-2 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700"
          >
            <Download size={18} /> Download Excel
          </button>
          <button
            onClick={() => setIsModalOpen(true)}
            className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
          >
            <Plus size={18} /> Add Project
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-4 mb-4 gap-2">
        <div className="relative w-full sm:w-1/3">
          <Search className="absolute left-3 top-2.5 text-gray-400" size={18} />
          <input
            type="text"
            placeholder="Search by project, customer, domain..."
            className="pl-9 pr-3 py-2 border rounded-lg w-full focus:ring focus:ring-blue-200"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        <select
          className="border rounded-lg px-3 py-2 w-full sm:w-1/5"
          value={category}
          onChange={(e) => setCategory(e.target.value)}
        >
          <option value="">Select Category</option>
          {allCategories.map((cat) => (
            <option key={cat} value={cat}>{cat}</option>
          ))}
        </select>

        <select
          className="border rounded-lg px-3 py-2 w-full sm:w-1/5"
          value={technology}
          onChange={(e) => setTechnology(e.target.value)}
        >
          <option value="">Select Technology</option>
          {allTechnologies.map((tech) => (
            <option key={tech} value={tech}>{tech}</option>
          ))}
        </select>

        <select
          className="border rounded-lg px-3 py-2 w-full sm:w-1/5"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          <option value="">All Status</option>
          <option value="active">Active</option>
          <option value="expire soon">Expire Soon</option>
          <option value="expired">Expired</option>
        </select>

        <button
          onClick={() => {
            setSearch("");
            setCategory("");
            setTechnology("");
            setStatusFilter("");
          }}
          className="bg-gray-300 hover:bg-gray-400 text-gray-800 px-3 py-2 rounded-lg"
        >
          Clear Filters
        </button>
      </div>

      {/* Table */}
      <div className="bg-white shadow rounded-lg overflow-x-auto">
        <table className="min-w-full text-sm text-left border-collapse">
          <thead className="bg-gray-100 text-gray-700">
            <tr>
              <th className="px-4 py-2 border">S.No.</th>
              <th className="px-4 py-2 border">Customer</th>
              {/* <th className="px-4 py-2 border">Contact</th> */}
              <th className="px-4 py-2 border">Project</th>
              <th className="px-4 py-2 border">Category</th>
              <th className="px-4 py-2 border">Technology</th>
              <th className="px-4 py-2 border">Domain</th>
              <th className="px-4 py-2 border">Purchase Date</th>
              <th className="px-4 py-2 border">Server</th>
              <th className="px-4 py-2 border">Status</th>
              <th className="px-4 py-2 border text-center">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="11" className="text-center py-6 text-gray-500">Loading...</td>
              </tr>
            ) : projects.length === 0 ? (
              <tr>
                <td colSpan="11" className="text-center py-6 text-gray-500">No projects found.</td>
              </tr>
            ) : (
              projects.map((p, index) => (
                <tr key={p.id} className="hover:bg-gray-50 transition">
                  <td className="px-4 py-2 border font-medium">{index + 1}</td>
                  <td className="px-4 py-2 border">{p.customer_name} <br />({p.contact_number})</td>
                  {/* <td className="px-4 py-2 border">{p.contact_number}</td> */}
                  <td className="px-4 py-2 border">{p.project_name}</td>
                  <td className="px-4 py-2 border">{p.project_category}</td>
                  <td className="px-4 py-2 border">{p.project_technology}</td>
                  <td className="px-4 py-2 border">{p.domain_name}</td>
                  <td className="px-4 py-2 border">{formatIndiaDate(p.domain_purchase_date)}</td>
                  <td className="px-4 py-2 border">{p.server_details}</td>
                  <td className="px-4 py-2 border">
                    <span className={`px-2 py-1 text-xs rounded-full font-medium ${badgeClass(p.domain_status)}`}>
                      {p.domain_status}
                    </span>
                  </td>
                  <td className="px-4 py-2 border text-center">
                    <div className="flex justify-center space-x-3">
                      <button
                        onClick={() => {
                          setSelectedProjectId(p.id);
                          setRenewModalOpen(true);
                        }}
                        className="p-2 bg-blue-100 text-blue-600 rounded-full hover:bg-blue-200"
                        title="Renew Domain"
                      >
                        Renew
                      </button>
                      <button
                        onClick={() => handleShareWhatsApp(p.domain_name)}
                        className="p-2 bg-green-100 text-green-600 rounded-full hover:bg-green-200"
                        title="Share via WhatsApp"
                      >
                        <FontAwesomeIcon icon={faWhatsapp} />
                      </button>
                      <button
                        onClick={() => handleDelete(p.id)}
                        className="p-2 bg-red-100 text-red-600 rounded-full hover:bg-red-200"
                        title="Delete"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Modals */}
      <AddProjectModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onProjectAdded={fetchProjects}
      />

      <RenewProjectModal
        isOpen={renewModalOpen}
        onClose={() => setRenewModalOpen(false)}
        projectId={selectedProjectId}
        onRenewed={fetchProjects}
      />
    </div>
  );
};

export default ProjectDetails;
